/**
 * CreationJourneyCard — floating bottom-left persistent checklist.
 *
 * Shows 5 ordered steps for a brand-new writer:
 *   Synopsis → Chapter Outline → Start Writing → AI Co-Writer → Character Bible
 *
 * Each step:
 *   • Has a "Go" button that fires the matching single-step spotlight tour
 *     AND dispatches the event that opens the relevant panel / dialog.
 *   • Is ticked off individually via localStorage once the user visits it.
 *
 * Visibility rules:
 *   • Auto-shows on first workspace load if the card has never been dismissed.
 *   • Re-shows once per browser session if the writer dismissed it before
 *     completing all 5 steps (sessionStorage flag prevents further re-shows).
 *   • Can always be reopened via the compass icon in the Activity Bar
 *     (event: 'loom:open-creation-journey').
 *   • Minimised state remembered in localStorage.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Map, Check, ChevronDown, ChevronUp, X, ArrowRight, Sparkles } from 'lucide-react';
import { runTour } from '@/lib/tours';

// ── persistence keys ───────────────────────────────────────────────────────
const KEY_DISMISSED = 'loom:cj-dismissed';      // permanent dismissal
const KEY_MINIMISED = 'loom:cj-minimised';      // collapsed vs expanded
const KEY_STEP      = (id) => `loom:cj-step-${id}-done`;
const SESSION_RESHOWN = 'loom_cj_reshown';      // one-per-session re-show guard

// ── step definitions ───────────────────────────────────────────────────────
const STEPS = [
  {
    id:      'synopsis',
    label:   'Synopsis Builder',
    hint:    "Define your story's spine before you write a word.",
    tourKey: 'cj-synopsis',
    action:  () => window.dispatchEvent(new CustomEvent('loom:open-synopsis')),
  },
  {
    id:      'outline',
    label:   'Chapter Outline',
    hint:    "Map your chapters — or let Claude scaffold them.",
    tourKey: 'cj-outline',
    action:  () => window.dispatchEvent(new CustomEvent('loom:open-panel', { detail: { panel: 'chapters' } })),
  },
  {
    id:      'write',
    label:   'Start Writing',
    hint:    "Open the editor and begin your first chapter.",
    tourKey: 'cj-write',
    action:  () => window.dispatchEvent(new CustomEvent('loom:open-panel', { detail: { panel: 'editor' } })),
  },
  {
    id:      'chat',
    label:   'AI Co-Writer',
    hint:    "Ask Claude to continue a scene or reshape a paragraph.",
    tourKey: 'cj-chat',
    action:  () => window.dispatchEvent(new CustomEvent('loom:open-panel', { detail: { panel: 'chat' } })),
  },
  {
    id:      'bible',
    label:   'Character Bible',
    hint:    "Add wounds, wants, and voice for every cast member.",
    tourKey: 'cj-bible',
    action:  () => window.dispatchEvent(new CustomEvent('loom:open-panel', { detail: { panel: 'bible' } })),
  },
];

// ── helpers ────────────────────────────────────────────────────────────────
function isDone(id) {
  try { return !!localStorage.getItem(KEY_STEP(id)); }
  catch { return false; }
}
function markDone(id) {
  try { localStorage.setItem(KEY_STEP(id), '1'); } catch { /* noop */ }
}
function isPermanentlyDismissed() {
  try { return !!localStorage.getItem(KEY_DISMISSED); }
  catch { return false; }
}

export default function CreationJourneyCard() {
  const [visible,   setVisible]   = useState(false);
  const [minimised, setMinimised] = useState(false);
  const [done,      setDone]      = useState(() => STEPS.map((s) => isDone(s.id)));
  // Re-render when EditorPanel mounts/unmounts so the window flag check is re-evaluated
  const [, forceRecheck] = useState(0);
  useEffect(() => {
    const handler = () => forceRecheck((n) => n + 1);
    window.addEventListener('loom:cj-state-changed', handler);
    return () => window.removeEventListener('loom:cj-state-changed', handler);
  }, []);

  // ── auto-show logic ──────────────────────────────────────────────────────
  useEffect(() => {
    if (isPermanentlyDismissed()) return;

    const allDone = STEPS.every((s) => isDone(s.id));
    if (allDone) return;                    // completed — never show again

    const collapsed = localStorage.getItem(KEY_MINIMISED) === '1';
    setMinimised(collapsed);
    setVisible(true);

    // Re-show once per session if the user previously minimised without finishing
    if (collapsed && !sessionStorage.getItem(SESSION_RESHOWN)) {
      sessionStorage.setItem(SESSION_RESHOWN, '1');
      setMinimised(false);                  // expand it once
    }
  }, []);

  // ── external open trigger (Activity Bar "Tour" button) ───────────────────
  useEffect(() => {
    const handler = () => {
      setVisible(true);
      setMinimised(false);
    };
    window.addEventListener('loom:open-creation-journey', handler);
    return () => window.removeEventListener('loom:open-creation-journey', handler);
  }, []);

  // ── actions ───────────────────────────────────────────────────────────────
  const handleGo = useCallback((step, idx) => {
    step.action();
    runTour(step.tourKey, { force: true });
    markDone(step.id);
    setDone((prev) => {
      const next = [...prev];
      next[idx] = true;
      return next;
    });
    window.dispatchEvent(new CustomEvent('loom:cj-state-changed'));
  }, []);

  const handleMinimise = useCallback(() => {
    setMinimised(true);
    try { localStorage.setItem(KEY_MINIMISED, '1'); } catch { /* noop */ }
    window.dispatchEvent(new CustomEvent('loom:cj-state-changed'));
  }, []);

  const handleExpand = useCallback(() => {
    setMinimised(false);
    try { localStorage.removeItem(KEY_MINIMISED); } catch { /* noop */ }
    window.dispatchEvent(new CustomEvent('loom:cj-state-changed'));
  }, []);

  const handleDismiss = useCallback(() => {
    setVisible(false);
    try { localStorage.setItem(KEY_DISMISSED, '1'); } catch { /* noop */ }
    window.dispatchEvent(new CustomEvent('loom:cj-state-changed'));
  }, []);

  const completedCount = done.filter(Boolean).length;
  const allComplete    = completedCount === STEPS.length;

  if (!visible) return null;

  // ── minimised pill ────────────────────────────────────────────────────────
  if (minimised) {
    // When EditorPanel is mounted, it renders its own Journey pill inside the
    // coach button row so everything stays in one horizontal line. Skip the
    // fixed pill here to avoid a duplicate.
    if (window.__loom_editor_journey_pill__) return null;
    return (
      <button
        type="button"
        onClick={handleExpand}
        data-testid="creation-journey-pill"
        title="Open Creation Journey"
        className="fixed bottom-6 left-4 z-50 flex items-center gap-2 rounded-full border px-3 py-1.5 text-[11.5px] font-semibold shadow-lg transition hover:brightness-110"
        style={{
          backgroundColor: '#1c1410',
          borderColor:     'rgba(251,191,36,0.55)',
          color:           'rgba(251,191,36,0.9)',
        }}
      >
        <Map className="h-3.5 w-3.5" />
        Journey {completedCount}/{STEPS.length}
        <ChevronUp className="h-3 w-3 opacity-70" />
      </button>
    );
  }

  // ── expanded card ─────────────────────────────────────────────────────────
  return (
    <div
      data-testid="creation-journey-card"
      className="fixed bottom-6 left-4 z-50 w-72 rounded-xl border shadow-2xl"
      style={{
        backgroundColor: '#1c1410',
        borderColor:     'rgba(251,191,36,0.45)',
      }}
    >
      {/* Header */}
      <div
        className="flex items-center gap-2 rounded-t-xl border-b px-4 py-2.5"
        style={{ borderColor: 'rgba(251,191,36,0.3)' }}
      >
        <Map className="h-4 w-4" style={{ color: 'rgba(251,191,36,0.85)' }} />
        <span
          className="flex-1 text-[12.5px] font-semibold tracking-wide"
          style={{ color: 'rgba(251,191,36,0.9)', fontFamily: '"Cormorant Garamond", serif' }}
        >
          Creation Journey
        </span>
        <span className="text-[11px]" style={{ color: 'rgba(251,191,36,0.55)' }}>
          {completedCount}/{STEPS.length}
        </span>
        <button
          type="button"
          onClick={handleMinimise}
          data-testid="cj-minimise-btn"
          aria-label="Minimise"
          className="rounded p-0.5 hover:bg-white/10"
          style={{ color: 'rgba(251,191,36,0.6)' }}
        >
          <ChevronDown className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          onClick={handleDismiss}
          data-testid="cj-dismiss-btn"
          aria-label="Dismiss permanently"
          className="rounded p-0.5 hover:bg-white/10"
          style={{ color: 'rgba(251,191,36,0.4)' }}
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Progress bar */}
      <div className="h-1 w-full overflow-hidden" style={{ backgroundColor: 'rgba(255,255,255,0.06)' }}>
        <div
          className="h-full transition-all duration-500"
          style={{
            width:           `${(completedCount / STEPS.length) * 100}%`,
            backgroundColor: allComplete ? '#22c55e' : 'rgba(251,191,36,0.75)',
          }}
        />
      </div>

      {/* Step list */}
      <ul className="py-2" data-testid="cj-step-list">
        {STEPS.map((step, idx) => {
          const stepDone = done[idx];
          return (
            <li
              key={step.id}
              data-testid={`cj-step-${step.id}`}
              className="flex items-start gap-3 px-4 py-2.5 transition hover:bg-white/5"
            >
              {/* Step indicator */}
              <div
                className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border text-[9.5px] font-bold"
                style={{
                  borderColor:     stepDone ? '#22c55e' : 'rgba(251,191,36,0.5)',
                  backgroundColor: stepDone ? '#14532d30' : 'transparent',
                  color:           stepDone ? '#22c55e'   : 'rgba(251,191,36,0.7)',
                }}
              >
                {stepDone ? <Check className="h-3 w-3" /> : idx + 1}
              </div>

              {/* Text */}
              <div className="flex-1 min-w-0">
                <p
                  className="text-[12px] font-semibold leading-tight"
                  style={{ color: stepDone ? 'rgba(255,255,255,0.4)' : 'rgba(255,255,255,0.88)' }}
                >
                  {step.label}
                </p>
                {!stepDone && (
                  <p className="mt-0.5 text-[10.5px] leading-snug" style={{ color: 'rgba(255,255,255,0.45)' }}>
                    {step.hint}
                  </p>
                )}
              </div>

              {/* Go button */}
              {!stepDone && (
                <button
                  type="button"
                  onClick={() => handleGo(step, idx)}
                  data-testid={`cj-go-${step.id}`}
                  className="shrink-0 inline-flex items-center gap-1 rounded-md px-2 py-1 text-[10.5px] font-semibold transition hover:brightness-110"
                  style={{ backgroundColor: 'rgba(251,191,36,0.15)', color: 'rgba(251,191,36,0.9)', border: '1px solid rgba(251,191,36,0.35)' }}
                >
                  Go <ArrowRight className="h-3 w-3" />
                </button>
              )}
            </li>
          );
        })}
      </ul>

      {/* All-done state */}
      {allComplete && (
        <div
          className="flex items-center gap-2 rounded-b-xl border-t px-4 py-2.5 text-[11.5px]"
          style={{ borderColor: 'rgba(34,197,94,0.3)', color: 'rgba(34,197,94,0.85)' }}
          data-testid="cj-complete-msg"
        >
          <Sparkles className="h-3.5 w-3.5" />
          All steps complete — you know The Loom.
        </div>
      )}
    </div>
  );
}
